// İSG uzmanı portalı — İş İzni Tipleri (Permit Types) erişim sondası (route guard UX).
//
// Asıl yetki backend'dedir: her uç `[ModuleAction("permit-types", …)]` taşır; grant'ı olmayan
// principal → 403 (api-contract §22). Bu sonda yalnız UX görünürlüğüdür — hafif bir liste probu
// (`GET /permit-types?page=1&pageSize=1`) çeker:
//   - 2xx  → erişim var (granted) → İş İzni Tipleri kabuğu + menü görünür
//   - 403  → yetki yok (forbidden) → gizli menü + 403 sayfası
//   - diğer (404/503/ağ) → servis/veri kullanılamıyor (unavailable) → yeniden dene
//
// İş İzni (§14 `usePermitsAccess`) sondasından AYRIDIR: gerçek modül Value = `permit-types`
// (küçük harf; §22) — probun bağladığı uç `list` aksiyonudur; yetkisiz principal buton/route göremez.

import { ApiError, permitTypesApi } from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { permitTypesKeys } from './permitTypesKeys';

export type PermitTypesAccessStatus = 'loading' | 'granted' | 'forbidden' | 'unavailable';

export interface PermitTypesAccess {
  status: PermitTypesAccessStatus;
  /** `unavailable` durumunda yeniden denemek için. */
  refetch: () => void;
}

/**
 * İş İzni Tipleri erişimini backend sondasıyla belirler. Oturum açık (RequireAuth altı) varsayılır.
 * 4xx'te retry yok (anında sonuç); 403 (yetki yok) ≠ 404/503 (servis/veri) ayrımı korunur.
 */
export function usePermitTypesAccess(): PermitTypesAccess {
  const query = useQuery({
    queryKey: permitTypesKeys.access(),
    queryFn: () => permitTypesApi.listPermitTypes({ page: 1, pageSize: 1 }),
    retry: false,
    staleTime: 5 * 60_000,
  });

  let status: PermitTypesAccessStatus;
  if (query.isPending) {
    status = 'loading';
  } else if (query.isSuccess) {
    status = 'granted';
  } else if (query.error instanceof ApiError && query.error.statusCode === 403) {
    status = 'forbidden';
  } else {
    status = 'unavailable';
  }

  return { status, refetch: () => void query.refetch() };
}
